import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ExternalLink, Clock, BookOpen, Sparkles, Send, Loader2,
  ChevronDown, MessageCircle, Award, FileText, Link2, GraduationCap,
  ClipboardCheck, Star, Save, MessageSquareText, Bot, ChevronUp, PlusCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { trackResourceClicked, trackPlanRefined } from "@/lib/analytics";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_BADGE_CLASS = {
  LevelUp: "badge-levelup",
  MSLearn: "badge-mslearn",
  MPN: "badge-mpn",
  Skilling_Hub: "badge-skilling",
  Other: "badge-other",
};

const CONTENT_FORMAT_CONFIG = {
  Course: { icon: GraduationCap, color: "bg-blue-50 text-blue-700 border-blue-200" },
  Certification: { icon: Award, color: "bg-amber-50 text-amber-700 border-amber-200" },
  "Applied Skill": { icon: ClipboardCheck, color: "bg-purple-50 text-purple-700 border-purple-200" },
  Guide: { icon: BookOpen, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PDF: { icon: FileText, color: "bg-red-50 text-red-700 border-red-200" },
  "External Link": { icon: Link2, color: "bg-gray-50 text-gray-700 border-gray-200" },
  Assessment: { icon: ClipboardCheck, color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
};

const SECTION_CONFIG = {
  start_here: { label: "Start Here", desc: "Priority resources to begin with", borderClass: "plan-section-start", color: "#07641D" },
  next: { label: "Up Next", desc: "Continue building your skills", borderClass: "plan-section-next", color: "#194449" },
  optional: { label: "Optional", desc: "Nice to have if you have extra time", borderClass: "plan-section-optional", color: "#B9DCD2" },
  certifications: { label: "Certifications & Assessments", desc: "Validate your skills with credentials", borderClass: "plan-section-cert", color: "#D97706" },
};

const PRODUCT_DISPLAY_NAMES = {
  microsoft_365_copilot: "Microsoft 365 Copilot",
  security_copilot: "Security Copilot",
  sentinel: "Microsoft Sentinel",
  defender_xdr: "Defender XDR",
  defender_for_cloud: "Defender for Cloud",
  microsoft_purview: "Microsoft Purview",
  microsoft_fabric: "Microsoft Fabric",
  microsoft_foundry: "Azure AI Foundry",
  dynamics_365_copilot: "Dynamics 365 Copilot",
  github_copilot: "GitHub Copilot",
  azure_openai: "Azure OpenAI",
  power_platform: "Power Platform",
  azure_infrastructure: "Azure Infrastructure",
  windows_365: "Windows 365",
  microsoft_teams: "Microsoft Teams",
  azure_databases: "Azure Databases",
  azure_ml: "Azure Machine Learning",
  azure_arc: "Azure Arc",
  microsoft_intune: "Microsoft Intune",
  sap_on_azure: "SAP on Azure",
  azure_databricks: "Azure Databricks",
  microsoft_copilot: "Microsoft Copilot",
};

// Solution-family groupings for sibling product suggestions
const SOLUTION_FAMILIES = {
  security: ["sentinel", "defender_xdr", "defender_for_cloud", "microsoft_purview", "security_copilot"],
  ai: ["microsoft_foundry", "azure_openai", "azure_ml", "azure_databricks", "github_copilot"],
  modern_work: ["microsoft_365_copilot", "microsoft_teams", "microsoft_intune", "windows_365"],
  data: ["microsoft_fabric", "azure_databases", "azure_databricks"],
  business_apps: ["dynamics_365_copilot", "power_platform"],
};

function generateContextPrompts(plan) {
  if (!plan) return ["Make it shorter", "Only beginner content", "Add more certifications"];

  const allCards = [
    ...(plan.start_here || []),
    ...(plan.next || []),
    ...(plan.optional || []),
    ...(plan.certifications || []),
  ];

  // Collect unique hero_products from all cards in the plan
  const planProducts = new Set();
  allCards.forEach((card) => {
    (card.hero_products || []).forEach((hp) => planProducts.add(hp.toLowerCase().replace(/\s+/g, "_")));
  });

  // Find the solution families represented in this plan
  const planFamilies = new Set();
  const siblingProducts = new Set();
  planProducts.forEach((prod) => {
    Object.entries(SOLUTION_FAMILIES).forEach(([family, members]) => {
      if (members.includes(prod)) {
        planFamilies.add(family);
        members.forEach((m) => siblingProducts.add(m));
      }
    });
  });

  // Build product-specific prompts: siblings present in plan get "Focus on X only"
  const productPrompts = [];
  siblingProducts.forEach((prod) => {
    const display = PRODUCT_DISPLAY_NAMES[prod];
    if (display) {
      productPrompts.push(`Focus on ${display} only`);
    }
  });

  // Keep only 3-4 most relevant product prompts (prioritize products actually in the plan)
  const sortedProductPrompts = productPrompts.sort((a, b) => {
    const aProd = Object.entries(PRODUCT_DISPLAY_NAMES).find(([, v]) => a.includes(v));
    const bProd = Object.entries(PRODUCT_DISPLAY_NAMES).find(([, v]) => b.includes(v));
    const aInPlan = aProd && planProducts.has(aProd[0]) ? 0 : 1;
    const bInPlan = bProd && planProducts.has(bProd[0]) ? 0 : 1;
    return aInPlan - bInPlan;
  });

  // Build non-technical prompts
  const genericPrompts = ["Make it shorter", "Only beginner content", "More hands-on labs"];

  // Check plan composition for relevant generic prompts
  const hasCerts = (plan.certifications || []).length > 0;
  const hasOnlyAdvanced = allCards.every((c) => c.skill_level === "Advanced");
  if (!hasCerts) genericPrompts.push("Add certification paths");
  if (hasOnlyAdvanced) genericPrompts.push("Include some beginner content");

  // Combine: up to 3 product prompts + 3 generic = max 6
  return [...sortedProductPrompts.slice(0, 3), ...genericPrompts.slice(0, 3)];
}

function ContentFormatBadge({ format }) {
  const config = CONTENT_FORMAT_CONFIG[format] || CONTENT_FORMAT_CONFIG["Course"];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${config.color}`} data-testid={`format-badge-${(format || '').replace(/\s/g, '-').toLowerCase()}`}>
      <Icon className="w-3 h-3" />
      {format}
    </span>
  );
}

function ResourceCard({ item, index, sessionId, userEmail }) {
  const contentFormat = item.content_format || "Course";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="bg-white rounded-xl border border-black/5 shadow-sm hover:shadow-md transition-all duration-300 p-5 group"
      data-testid={`resource-card-${item.resource_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <ContentFormatBadge format={contentFormat} />
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE_CLASS[item.resource_type] || TYPE_BADGE_CLASS.Other}`}>
              {(item.resource_type || "").replace("_", " ")}
            </span>
            {item.skill_level && (
              <span className="text-xs text-[#64748B]">{item.skill_level}</span>
            )}
            {item.exam_code && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-mono font-medium">{item.exam_code}</span>
            )}
          </div>
          <h4 className="font-medium text-[#091F2C] text-sm leading-snug">{item.title || item.resource_id}</h4>
        </div>
        {item.estimated_time_minutes > 0 && (
          <div className="flex items-center gap-1 text-xs text-[#64748B] shrink-0">
            <Clock className="w-3.5 h-3.5" />
            {item.estimated_time_minutes >= 60
              ? `${(item.estimated_time_minutes / 60).toFixed(1)}h`
              : `${item.estimated_time_minutes}m`
            }
          </div>
        )}
      </div>
      <p className="text-xs text-[#64748B] leading-relaxed mb-3">{item.reason}</p>
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1">
          {(item.topic_tags || []).slice(0, 3).map(tag => (
            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-[#F4F3F5] text-[#64748B]">
              {tag.replace("_", " ")}
            </span>
          ))}
        </div>
        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackResourceClicked(sessionId, item.resource_id, userEmail)}
            className="inline-flex items-center gap-1 text-xs font-medium text-[#194449] hover:text-[#07641D] transition-colors"
            data-testid={`resource-link-${item.resource_id}`}
          >
            Open <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </motion.div>
  );
}

// Star Rating Component
function StarRating({ rating, onRate, disabled }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => !disabled && onRate(star)}
          onMouseEnter={() => !disabled && setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          disabled={disabled}
          className="p-0.5 transition-transform hover:scale-110 disabled:cursor-default"
          data-testid={`star-${star}`}
        >
          <Star
            className={`w-6 h-6 transition-colors ${
              star <= (hovered || rating)
                ? "fill-amber-400 text-amber-400"
                : "fill-transparent text-[#CBD5E1]"
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function LearningPlanPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [planData, setPlanData] = useState(location.state?.plan || null);
  const [loading, setLoading] = useState(!planData);
  const [refinementMsg, setRefinementMsg] = useState("");
  const [refining, setRefining] = useState(false);
  const [refinementsRemaining, setRefinementsRemaining] = useState(2);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  // Live preview of what the deterministic refine engine will detect
  const [refinePreview, setRefinePreview] = useState(null);

  // Post-plan state
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [starRating, setStarRating] = useState(0);
  const [ratingSaved, setRatingSaved] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [planSaved, setPlanSaved] = useState(false);
  const [showPostPlan, setShowPostPlan] = useState(true);
  const postPlanRef = useRef(null);

  const scrollToPostPlan = (section) => {
    setShowPostPlan(true);
    // Small delay so the section expands before scrolling
    setTimeout(() => {
      postPlanRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    if (section === "save") {
      setTimeout(() => document.getElementById("save-plan-input")?.focus(), 300);
    }
  };

  useEffect(() => {
    if (!planData && sessionId) {
      setLoading(true);
      const token = localStorage.getItem("app_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      axios.get(`${API}/plans/${sessionId}`, { headers })
        .then(res => {
          if (res.data?.plan) {
            setPlanData(res.data);
            const used = res.data.refinement_count || 0;
            setRefinementsRemaining(Math.max(0, 2 - used));
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [sessionId, planData]);

  const plan = planData?.plan;

  // Debounced preview — show "we'll add: X · format → Y" chip below input
  useEffect(() => {
    const msg = (refinementMsg || "").trim();
    if (!msg || !sessionId) {
      setRefinePreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/plans/refine/preview`, {
          session_id: sessionId,
          message: msg,
        });
        setRefinePreview(res.data);
      } catch {
        setRefinePreview(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [refinementMsg, sessionId]);

  const handleRefine = async (msg) => {
    const message = msg || refinementMsg.trim();
    if (!message) return;

    setRefining(true);
    setChatHistory(prev => [...prev, { role: "user", text: message }]);
    setRefinementMsg("");

    try {
      const res = await axios.post(`${API}/plans/refine`, {
        session_id: sessionId,
        message: message,
      });
      setPlanData(prev => ({ ...prev, plan: res.data.plan }));
      setRefinementsRemaining(res.data.refinements_remaining);
      setRefinePreview(null);
      trackPlanRefined(sessionId, { message: message }, user?.email);
      const validation = res.data.plan?._refinement_applied?.validation || {};
      const before = validation.duration_before_hours;
      const after = validation.duration_after_hours;
      let reply = "Plan updated based on your request.";
      if (before && after && before !== after) {
        reply = `Plan updated — total duration is now ${after}h (was ${before}h).`;
      }
      if (validation.unmet_message) {
        reply += ` ${validation.unmet_message}`;
      }
      setChatHistory(prev => [...prev, { role: "ai", text: reply }]);
      if (validation.unmet_message) {
        toast.warning(validation.unmet_message, { duration: 8000 });
      } else {
        toast.success(before && after && before !== after
          ? `Plan refined — duration now ${after}h (was ${before}h)`
          : "Plan refined successfully");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Refinement failed";
      setChatHistory(prev => [...prev, { role: "ai", text: `Error: ${errorMsg}` }]);
      toast.error(errorMsg);
    } finally {
      setRefining(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim()) return;
    try {
      await axios.post(`${API}/plans/${sessionId}/feedback`, { feedback_text: feedbackText.trim() });
      setFeedbackSaved(true);
      toast.success("Feedback submitted");
    } catch { toast.error("Failed to save feedback"); }
  };

  const handleRating = async (rating) => {
    setStarRating(rating);
    try {
      await axios.post(`${API}/plans/${sessionId}/rate`, { star_rating: rating });
      setRatingSaved(true);
      toast.success("Rating saved");
    } catch { toast.error("Failed to save rating"); }
  };

  const handleSavePlan = async () => {
    if (!saveTitle.trim()) return;
    try {
      const headers = {};
      const appToken = localStorage.getItem("app_token");
      if (appToken) headers.Authorization = `Bearer ${appToken}`;
      await axios.post(`${API}/plans/${sessionId}/save`, { title: saveTitle.trim() }, { headers });
      setPlanSaved(true);
      toast.success("Plan saved!");
    } catch { toast.error("Failed to save plan"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#194449] mx-auto mb-4" />
          <p className="text-[#64748B]">Loading your plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#FFF8F3] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-[#B9DCD2] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#091F2C] mb-2">No plan found</h2>
          <p className="text-[#64748B] mb-6">Start a new intake to generate your learning plan.</p>
          <Button onClick={() => navigate("/intake")} className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-6" data-testid="go-to-intake-btn">
            Start New Plan
          </Button>
        </div>
      </div>
    );
  }

  const allItems = [...(plan.start_here || []), ...(plan.next || []), ...(plan.optional || []), ...(plan.certifications || [])];
  const totalMinutes = allItems.reduce((acc, item) => acc + (item.estimated_time_minutes || 0), 0);
  const totalHours = totalMinutes > 0 ? (totalMinutes / 60).toFixed(1) : null;
  const planDurationHours = plan.plan_duration_hours || plan.plan_duration || totalHours || "~10";

  const formatCounts = {};
  allItems.forEach(item => {
    const fmt = item.content_format || "Course";
    formatCounts[fmt] = (formatCounts[fmt] || 0) + 1;
  });

  return (
    <div className="min-h-screen bg-[#FFF8F3]">
      <header className="sticky top-0 z-50 w-full bg-[#194449] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/my-plans")} className="flex items-center gap-3" data-testid="logo-home-link-plan">
            <img src="/technofocus-logo-white.png" alt="Technofocus" className="h-6 brightness-0 invert" />
            <div className="w-px h-6 bg-white/30" />
            <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>MyGoals</span>
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/my-plans")}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors hidden sm:block"
              data-testid="my-goals-link-plan"
            >
              My Goals
            </button>
            <img src="/ms-sponsored-logo.svg" alt="Sponsored by Microsoft" className="h-8 hidden md:block" />
          </div>
        </div>
      </header>

      {/* Copilot Refinement Banner - Prominent top placement */}
      <div className="bg-gradient-to-r from-[#194449] to-[#07641D] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-[#D4EC8E]" />
            <span className="text-sm font-medium">Want to adjust your plan? Use Copilot to refine it.</span>
          </div>
          <Button
            onClick={() => setChatOpen(true)}
            variant="ghost"
            className="text-white bg-white/10 hover:bg-white/20 rounded-full px-4 h-8 text-xs font-medium gap-1.5"
            disabled={refinementsRemaining <= 0}
            data-testid="copilot-refine-banner-btn"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Refine with Copilot
            {refinementsRemaining > 0 && <span className="text-[#D4EC8E]">({refinementsRemaining} left)</span>}
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8">
        {/* Plan Header */}
        <div className="mb-8">
          <button onClick={() => navigate("/intake")} className="inline-flex items-center text-sm text-[#64748B] hover:text-[#194449] mb-4 transition-colors" data-testid="back-to-intake">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Intake
          </button>

          {/* Title row with quick actions */}
          <div className="flex items-start justify-between gap-4 mb-3">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Learning Plan
            </h1>
            {/* Quick action pills */}
            <div className="flex items-center gap-2 shrink-0 mt-1">
              <button
                onClick={() => scrollToPostPlan("rate")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-300 transition-all duration-200"
                data-testid="quick-rate-btn"
              >
                <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                Rate plan
              </button>
              <button
                onClick={() => scrollToPostPlan("save")}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#194449]/20 bg-white text-[#194449] hover:bg-[#194449] hover:text-white hover:border-[#194449] transition-all duration-200"
                data-testid="quick-save-btn"
              >
                <Save className="w-3.5 h-3.5" />
                Save plan
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 items-center mb-4">
            <Badge variant="secondary" className="bg-[#D4EC8E] text-[#091F2C] border-0 px-3 py-1 flex items-center gap-1.5" data-testid="plan-duration-badge">
              <Clock className="w-3.5 h-3.5" />
              ~{planDurationHours} hours
            </Badge>
            <span className="text-sm text-[#64748B]" data-testid="plan-items-count">
              {allItems.length} resources
            </span>
            {planData?.duration_ms && (
              <span className="text-xs text-[#B9DCD2]">
                Generated in {(planData.duration_ms / 1000).toFixed(1)}s
              </span>
            )}
          </div>

          {Object.keys(formatCounts).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4" data-testid="content-format-summary">
              {Object.entries(formatCounts).map(([fmt, count]) => (
                <span key={fmt} className="text-xs px-2.5 py-1 rounded-full bg-white border border-black/10 text-[#64748B] font-medium">
                  {count} {fmt}{count > 1 ? 's' : ''}
                </span>
              ))}
            </div>
          )}

          {plan.summary && (
            <p className="text-[#64748B] leading-relaxed max-w-3xl">{plan.summary}</p>
          )}
          {plan.skill_focus_tags && plan.skill_focus_tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {plan.skill_focus_tags.map(tag => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-[#194449]/10 text-[#194449] font-medium">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Plan Sections */}
        <div className="space-y-8 mb-12">
          {Object.entries(SECTION_CONFIG).map(([key, config]) => {
            const items = plan[key] || [];
            if (items.length === 0) return null;
            return (
              <div key={key} data-testid={`plan-section-${key}`}>
                <div className={`${config.borderClass || 'pl-4 border-l-4 border-[#194449]'} pl-4 mb-4`}>
                  <h2 className="text-xl font-semibold text-[#091F2C] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {key === "certifications" && <Award className="w-5 h-5 text-amber-600" />}
                    {config.label}
                  </h2>
                  <p className="text-sm text-[#64748B]">{config.desc}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {items.map((item, i) => (
                    <ResourceCard key={item.resource_id} item={item} index={i} sessionId={sessionId} userEmail={user?.email} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Post-Plan Experience Section */}
        <div ref={postPlanRef} className="border-t border-[#E2E8F0] pt-8 mb-16 space-y-8">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-[#091F2C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How was your experience?
            </h2>
            <button onClick={() => setShowPostPlan(!showPostPlan)} className="text-[#64748B] hover:text-[#194449]">
              {showPostPlan ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>

          {showPostPlan && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              {/* Rating */}
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-6" data-testid="rating-section">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-[#091F2C] mb-1">Rate this learning plan</h3>
                    <p className="text-xs text-[#64748B]">Your rating helps us improve recommendations.</p>
                  </div>
                  <StarRating rating={starRating} onRate={handleRating} disabled={ratingSaved} />
                </div>
                {ratingSaved && <p className="text-xs text-[#07641D] mt-2">Thank you for your rating!</p>}
              </div>

              {/* Feedback */}
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-6" data-testid="feedback-section">
                <div className="flex items-start gap-3 mb-3">
                  <MessageSquareText className="w-5 h-5 text-[#194449] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-[#091F2C] mb-1">Share your feedback</h3>
                    <p className="text-xs text-[#64748B] mb-3">
                      Are there topics you'd like to see additionally? Parts of the plan that don't apply to your goals?
                    </p>
                    <textarea
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="Tell us how we can improve this plan for you..."
                      className="w-full h-24 p-3 rounded-lg border border-[#E2E8F0] bg-[#FAFAFA] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#194449]/20 focus:border-[#194449]"
                      disabled={feedbackSaved}
                      data-testid="feedback-textarea"
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        onClick={handleFeedbackSubmit}
                        disabled={!feedbackText.trim() || feedbackSaved}
                        className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-5 h-9 text-xs"
                        data-testid="feedback-submit-btn"
                      >
                        {feedbackSaved ? "Submitted" : "Submit Feedback"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Plan */}
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-6" data-testid="save-plan-section">
                <div className="flex items-start gap-3">
                  <Save className="w-5 h-5 text-[#194449] mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-[#091F2C] mb-1">Save this plan</h3>
                    <p className="text-xs text-[#64748B] mb-3">
                      {localStorage.getItem("app_token")
                        ? "Give it a name so you can find it in your dashboard."
                        : "Sign in with LevelUp to save plans to your dashboard."}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        id="save-plan-input"
                        value={saveTitle}
                        onChange={(e) => setSaveTitle(e.target.value)}
                        placeholder="e.g., My Azure AI Learning Path"
                        className="flex-1 h-9 bg-[#FAFAFA] border-[#E2E8F0] text-sm"
                        disabled={planSaved}
                        data-testid="save-plan-title-input"
                      />
                      <Button
                        onClick={handleSavePlan}
                        disabled={!saveTitle.trim() || planSaved}
                        className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-5 h-9 text-xs"
                        data-testid="save-plan-btn"
                      >
                        {planSaved ? "Saved!" : "Save"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* New Plan CTA - Bottom placement */}
        <div className="border-t border-[#E2E8F0] pt-8 pb-12 text-center" data-testid="new-plan-bottom-section">
          <p className="text-sm text-[#64748B] mb-4">Ready to explore a different learning path?</p>
          <Button
            onClick={() => navigate("/intake")}
            className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full px-8 h-11 text-sm font-medium gap-2 shadow-md hover:shadow-lg transition-all"
            data-testid="new-plan-btn"
          >
            <PlusCircle className="w-4 h-4" />
            Create New Plan
          </Button>
        </div>
      </div>

      {/* Refinement Chat - Floating Panel */}
      <div className="fixed bottom-6 right-6 z-40">
        {!chatOpen ? (
          <Button
            onClick={() => setChatOpen(true)}
            className="bg-[#194449] text-white hover:bg-[#07641D] rounded-full w-14 h-14 p-0 shadow-lg hover:shadow-xl transition-all"
            disabled={refinementsRemaining <= 0}
            data-testid="open-chat-btn"
          >
            <Bot className="w-6 h-6" />
          </Button>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="w-[380px] bg-white rounded-2xl shadow-2xl border border-black/10 overflow-hidden"
          >
            <div className="bg-[#194449] text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Bot className="w-5 h-5 text-[#D4EC8E]" />
                <div>
                  <div className="font-medium text-sm">Refine with Copilot</div>
                  <div className="text-xs text-white/70">{refinementsRemaining} refinement{refinementsRemaining !== 1 ? 's' : ''} remaining</div>
                </div>
              </div>
              <button onClick={() => setChatOpen(false)} className="text-white/70 hover:text-white" data-testid="close-chat-btn">
                <ChevronDown className="w-5 h-5" />
              </button>
            </div>

            <div className="px-4 py-3 border-b border-black/5 flex flex-wrap gap-1.5">
              {generateContextPrompts(plan).map(q => (
                <button
                  key={q}
                  onClick={() => handleRefine(q)}
                  disabled={refining || refinementsRemaining <= 0}
                  className="text-xs px-3 py-1.5 rounded-full bg-[#F4F3F5] text-[#64748B] hover:bg-[#D4EC8E] hover:text-[#091F2C] transition-colors disabled:opacity-50"
                  data-testid={`quick-refine-${q.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {q}
                </button>
              ))}
            </div>

            <div className="h-[200px] overflow-y-auto px-4 py-3 space-y-3">
              {chatHistory.length === 0 && (
                <p className="text-xs text-[#64748B] text-center mt-8">
                  Ask me to adjust your plan — shorter, different focus, or different level.
                </p>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`${msg.role === 'user' ? 'chat-user ml-8' : 'chat-ai mr-8'} px-3 py-2`}>
                  <p className="text-sm text-[#091F2C]">{msg.text}</p>
                </div>
              ))}
              {refining && (
                <div className="chat-ai mr-8 px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#194449]" />
                </div>
              )}
            </div>

            <div className="border-t border-black/5 px-4 py-3">
              <form onSubmit={e => { e.preventDefault(); handleRefine(); }} className="flex gap-2">
                <Input
                  value={refinementMsg}
                  onChange={e => setRefinementMsg(e.target.value)}
                  placeholder={refinementsRemaining > 0 ? "e.g., Focus on certification prep..." : "No refinements remaining"}
                  className="flex-1 h-10 bg-[#F4F3F5] border-0 text-sm"
                  disabled={refining || refinementsRemaining <= 0}
                  data-testid="refinement-input"
                />
                <Button
                  type="submit"
                  disabled={refining || !refinementMsg.trim() || refinementsRemaining <= 0}
                  className="bg-[#194449] text-white hover:bg-[#07641D] h-10 w-10 p-0 rounded-lg"
                  data-testid="send-refinement-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              {/* Live detection preview chip — only show when there's something interesting */}
              {refinePreview && (refinePreview.products?.length > 0 || refinePreview.format_shift || refinePreview.wants_remove) && (
                <div
                  className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] text-[#64748B]"
                  data-testid="refine-preview-chip"
                >
                  <span className="text-[#64748B]">Detected:</span>
                  {refinePreview.products?.map(p => (
                    <span
                      key={p.id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#194449]/10 text-[#194449] font-medium"
                      data-testid={`refine-preview-product-${p.id}`}
                    >
                      + {p.label}
                    </span>
                  ))}
                  {refinePreview.format_shift && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#07641D]/10 text-[#07641D] font-medium"
                      data-testid="refine-preview-format"
                    >
                      Format → {refinePreview.format_shift.replace("_", " ")}
                    </span>
                  )}
                  {refinePreview.wants_remove && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium"
                      data-testid="refine-preview-remove"
                    >
                      Remove matching
                    </span>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
